import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { registerActionGuard, runSystemAction } from '../engine/workflowEngine'
import { packPer } from '../seed/wms-fg.seed'
import { findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import {
  fail,
  handle,
  idOf,
  nextDocNo,
  pad,
  parse,
  requireRole,
  roleCodesOf,
  statusCodeOf,
  txLog,
  workflowRunner
} from './c07.shared'
import { checkClaimComplete } from './claimPutaway'
import { notify, wmsRoute } from './notification.shared'
import { REPACK_ORIGIN } from './repackGuard'
import { escalateHours, slaEnabled, slaHours } from './slaSetting'

/**
 * Endpoint custom C08 Inbound FG (WF-FG-01, entity `fg_labeler_batch`) — `/api/v1/wms/inbound-fg/*`:
 * - IFG-01 `refresh-labeler` (kiriman Auto Labeler → batch Ready), `submit-check` (Terima & Cek → Menunggu Approval),
 *   `sla-sweep` (auto-approve 8 jam), `assign-putaway` (Disetujui → Assigned, satu tugas FPW per operator).
 * - IFG-03 `reassign` (Edit Assign).
 * - IFG-04 `putaway-gate` (aturan lokasi), `putaway-place` (satu unit), `putaway-place-pack` (qty Pack ke container),
 *   `rack-full`, `correct-placement` (koreksi penempatan FG).
 * Keputusan Supervisor/Kadiv (Approve/Tahan/Kembalikan) memakai `POST /api/workflow/submission/v2` standar.
 * v7.50 C-09: tugas putaway sumber CLAIM (`source_type = 'CLAIM'`, `claim_id`, `sku_id`, tanpa batch) memakai layar
 * IFG-04 yang sama. Item Repack / Not Good terkunci (`is_target_locked`) ke satu pallet — aturan pallet produksi tidak
 * berlaku, pallet lain ditolak ("Barang ini hanya boleh disimpan di …"). Unit Good utuh memakai aturan biasa, pack Good ke
 * container Pack. Selesai: Good / Repack → `fg_pack_stock` (tanda Retur / Repack), Not Good → `fg_ng_pack` di karantina
 * (Menunggu Keputusan pemusnahan), lalu claim Selesai otomatis bila semua tugasnya selesai.
 */

const WORKFLOW_CODE = 'WF-FG-01'
const fgFlow = workflowRunner(WORKFLOW_CODE, 'fg_labeler_batch')
/** Bawaan kapasitas pallet; tiap pallet membawa `capacity`/`recommended` sendiri dari Master Lokasi Gudang. */
const REC = 18
const CAP = 24
const palRec = (row: LocalRow) => Number(row.recommended) || REC
const palCap = (row: LocalRow) => Number(row.capacity) || CAP
/** Ambang auto-approve (jam) — dibaca dari Master SLA `FG_APPROVE_AUTO`. */
export const fgAutoApproveHours = () => slaHours('FG_APPROVE_AUTO')
const SKU_SHORT: Record<string, string> = {
  'TBC-ONE-12': 'TO12',
  'TBC-BOLD-20': 'TB20',
  'TBC-BOLD-20-SKT': 'TB20T',
  'TBC-XTRA-20': 'TX20'
}
const FEED_POOL = ['TBC-BOLD-20', 'TBC-XTRA-20', 'TBC-ONE-12']

const submitActionId = () => {
  const wf = readTable('wml_workflow').find(row => row.code === WORKFLOW_CODE)
  return String(
    readTable('wml_action').find(row => row.name === 'ACT_FG_SUBMIT' && idOf(row.workflow_id) === wf?.id)?.id ?? ''
  )
}

const batchOf = (id: unknown) => {
  const row = findRow('fg_labeler_batch', String(id ?? ''))
  if (!row) fail('Batch tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return row
}
const skuOf = (batch: LocalRow) => findRow('mt_product', idOf(batch.sku_id)) as LocalRow
const labelsOf = (batch: LocalRow, level?: string) =>
  readTable('fg_label').filter(label => idOf(label.batch_id) === batch.id && (!level || label.level === level))
const tasksOf = (batch: LocalRow) => readTable('fg_putaway_task').filter(task => idOf(task.batch_id) === batch.id)
const itemsOf = (task: LocalRow) => readTable('fg_putaway_task_item').filter(item => idOf(item.task_id) === task.id)
const palletByCode = (code: string) =>
  readTable('pallet').find(row => row.pallet_kind === 'FG' && String(row.code).toUpperCase() === code.toUpperCase())
const containerByCode = (code: string) =>
  readTable('container').find(row => String(row.code).toUpperCase() === code.toUpperCase())
const up = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase()

const actual = (batch: LocalRow, field: 'karton' | 'bal' | 'slop' | 'pack') => {
  const checked = batch[`qty_${field}_actual`]
  return Number(checked ?? batch[`qty_${field}`] ?? 0)
}

/* ------------------------------------------------------------ rekomendasi pallet (tangga JalCalc.palSaranUnit) */

type Reserved = Map<string, number>

export function recommendPallet(skuId: string, unitType: string, reserved: Reserved, skip: string[] = []) {
  const pallets = readTable('pallet')
    .filter(
      row =>
        row.pallet_kind === 'FG' &&
        row.is_active !== false &&
        row.pallet_status !== 'FULL' &&
        !skip.includes(String(row.code))
    )
    .sort((a, b) => String(a.code).localeCompare(String(b.code)))
  const eff = (row: LocalRow) => Number(row.occupied ?? 0) + (reserved.get(String(row.code)) ?? 0)
  const sameSku = (row: LocalRow) =>
    eff(row) > 0 && idOf(row.sku_id) === skuId && (!row.unit_type || row.unit_type === unitType)
  const pick =
    pallets.find(row => sameSku(row) && eff(row) < palRec(row)) ??
    pallets.find(row => eff(row) === 0 && !row.sku_id) ??
    pallets.find(row => sameSku(row) && eff(row) < palCap(row)) ??
    null
  if (pick) reserved.set(String(pick.code), (reserved.get(String(pick.code)) ?? 0) + 1)
  return pick
}

/** Container jenis kemasan yang cocok (Slop / Pack). */
export const containerFor = (unitType: string) =>
  readTable('container').find(row => row.container_type === unitType && row.is_active !== false)

/* ------------------------------------------------------------ aturan lokasi (gerbang IFG-04) */

export type GateVerdict = {
  verdict: 'ok' | 'needs_reason' | 'reject'
  title: string
  message: string
  location_kind?: 'pallet' | 'container'
  occupied?: number
  /** Tujuan yang seharusnya dipindai (barang claim terkunci). */
  expected?: string
}

function checkLocation(task: LocalRow, sku: string, unitType: string, code: string, recommended: string): GateVerdict {
  const pallet = palletByCode(code)
  const container = containerByCode(code)
  if (!pallet && !container)
    return { verdict: 'reject', title: 'Lokasi Tidak Dikenal', message: `${code} bukan pallet atau container FG.` }
  if (container) {
    if (unitType === 'Karton' || unitType === 'Bal')
      return {
        verdict: 'reject',
        title: 'Bukan Pallet',
        message: `${unitType} disimpan di pallet, bukan container (${container.code}).`,
        location_kind: 'container'
      }
    if (container.is_active === false)
      return {
        verdict: 'reject',
        title: 'Container Nonaktif',
        message: `${container.code} sudah dinonaktifkan di Master Lokasi Gudang — pilih container lain.`,
        location_kind: 'container'
      }
    if (container.container_type !== unitType)
      return {
        verdict: 'reject',
        title: 'Jenis Container Salah',
        message:
          unitType === 'Pack'
            ? 'Pack disimpan di container, bukan pallet.'
            : `${container.code} khusus ${container.container_type}; ${unitType} tidak boleh satu container dengan ${container.container_type}.`,
        location_kind: 'container'
      }
    return code.toUpperCase() === recommended.toUpperCase()
      ? { verdict: 'ok', title: 'Container Benar', message: '', location_kind: 'container' }
      : {
          verdict: 'needs_reason',
          title: 'Bukan Container Rekomendasi',
          message: `Rekomendasi ${recommended}, Anda memindai ${container.code}.`,
          location_kind: 'container'
        }
  }
  const row = pallet as LocalRow
  const occupied = Number(row.occupied ?? 0)
  if (row.is_active === false)
    return {
      verdict: 'reject',
      title: 'Pallet Nonaktif',
      message: `${row.code} sudah dinonaktifkan di Master Lokasi Gudang — pilih pallet lain.`,
      location_kind: 'pallet',
      occupied
    }
  if (unitType === 'Slop' || unitType === 'Pack')
    return {
      verdict: 'reject',
      title: 'Bukan Container',
      message:
        unitType === 'Pack' ? 'Pack disimpan di container, bukan pallet.' : 'Slop disimpan di container, bukan pallet.',
      location_kind: 'pallet'
    }
  if (occupied > 0 && row.sku_id && idOf(row.sku_id) !== sku)
    return {
      verdict: 'reject',
      title: 'Pallet Berisi SKU Lain',
      message: `${row.code} sudah berisi ${findRow('mt_product', idOf(row.sku_id))?.code ?? 'SKU lain'}.`,
      location_kind: 'pallet',
      occupied
    }
  if (occupied > 0 && row.unit_type && row.unit_type !== unitType)
    return {
      verdict: 'reject',
      title: 'Karton Tidak Boleh Dicampur Bal Lepas',
      message: `${row.code} berisi ${row.unit_type}; ${unitType} tidak boleh dicampur.`,
      location_kind: 'pallet',
      occupied
    }
  if (occupied >= palCap(row))
    return {
      verdict: 'reject',
      title: 'Melebihi Kapasitas',
      message: `${row.code} sudah ${occupied} dari kapasitas ${palCap(row)} — tidak bisa ditambah.`,
      location_kind: 'pallet',
      occupied
    }
  if (occupied >= palRec(row))
    return {
      verdict: 'needs_reason',
      title: 'Melebihi Rekomendasi',
      message: `${row.code} sudah ${occupied} dari rekomendasi ${palRec(row)}. Lanjutkan dan catat alasan?`,
      location_kind: 'pallet',
      occupied
    }
  if (code.toUpperCase() !== recommended.toUpperCase())
    return {
      verdict: 'needs_reason',
      title: 'Bukan Pallet Rekomendasi',
      message: `Rekomendasi ${recommended}, Anda memindai ${row.code}.`,
      location_kind: 'pallet',
      occupied
    }
  return { verdict: 'ok', title: 'Pallet Benar', message: '', location_kind: 'pallet', occupied }
}

const targetCodeOf = (item: LocalRow) =>
  String(
    findRow('pallet', idOf(item.target_pallet_id))?.code ??
      findRow('container', idOf(item.target_container_id))?.code ??
      ''
  )

/* ------------------------------------------------------------ IFG-01 */

export function refreshLabeler() {
  requireRole('Refresh data labeler hanya untuk Admin WH FG.', 'admin_wh_fg')
  const feeds = readTable('fg_labeler_feed_log')
  const sku = FEED_POOL[feeds.length % FEED_POOL.length]
  const product = readTable('mt_product').find(row => row.code === sku) as LocalRow
  const date = new Date()
  const ymd = `${date.getFullYear()}${pad(date.getMonth() + 1, 2)}${pad(date.getDate(), 2)}`
  const feedRef = `ALF-${ymd.slice(2)}-${pad(feeds.filter(f => String(f.external_batch_id).includes(ymd.slice(2))).length + 1, 3)}`
  const maxSeq = readTable('fg_labeler_batch').reduce((max, row) => {
    const seq = Number(String(row.batch_no).split('-')[2] ?? 0)
    return Math.max(max, seq)
  }, 0)
  const batchNo = `${SKU_SHORT[sku]}-${ymd}-${pad(maxSeq + 1, 4)}-B001`
  const k = 4
  const b = 1
  const per = packPer(sku)
  const batchId = newId()
  const { submissionId, statusId } = fgFlow.start(batchId)
  insertRow('fg_labeler_batch', {
    ...auditFieldsForCreate(),
    id: batchId,
    batch_no: batchNo,
    labeler_ref: feedRef,
    sku_id: product.id,
    prod_date: `${date.getFullYear()}-${pad(date.getMonth() + 1, 2)}-${pad(date.getDate(), 2)}`,
    shift: '2',
    source: 'LINK',
    machine_id: readTable('mt_equipment').find(row => row.code === 'MOLLINS-2')?.id ?? null,
    spk_ref: null,
    qty_karton: k,
    qty_bal: b,
    qty_slop: 0,
    qty_pack: 0,
    qty_karton_actual: null,
    qty_bal_actual: null,
    qty_slop_actual: null,
    qty_pack_actual: null,
    equivalent_pack: k * per.karton + b * per.bal,
    label_ok: null,
    checked_at: null,
    status_id: statusId,
    submission_id: submissionId
  })
  const head = batchNo.split('-')
  const labelRow = (level: string, letter: string, seq: number) =>
    insertRow('fg_label', {
      ...auditFieldsForCreate(),
      id: newId(),
      label_no: `JLC:${letter}:F:${head[0]}:${head[2]}:${head[1]}:${pad(date.getHours(), 2)}${pad(date.getMinutes(), 2)}${pad(seq % 60, 2)}:B001:${pad(seq, 4)}`,
      sku_id: product.id,
      batch_id: batchId,
      level,
      gtin: null,
      parent_label_id: null,
      zone_id: null,
      pallet_id: null,
      container_id: null,
      label_status: 'LABELED',
      is_priority_lot: false
    })
  for (let i = 0; i < k; i++) labelRow('Karton', 'C', 501 + i)
  for (let i = 0; i < b; i++) labelRow('Bal', 'B', 2031 + i)
  insertRow('fg_labeler_feed_log', {
    ...auditFieldsForCreate(),
    id: newId(),
    external_batch_id: feedRef,
    batch_id: batchId,
    raw_payload: { sku, shift: 2, machine: 'MOLLINS-2', k, b },
    validation_result: 'OK',
    error_message: null,
    received_at: new Date().toISOString()
  })
  const created = findRow('fg_labeler_batch', batchId) as LocalRow
  fgFlow.run(created, 'ACT_FG_FEED_RECV', ['ACT_FG_ADMIN_FG'])
  txLog('FG Auto Labeler', 'IFG-01', 'alf-feed', batchNo, `Kiriman ${feedRef} masuk — ${sku}, ${k} Karton + ${b} Bal`)
  return { made: [batchNo], feed_ref: feedRef }
}

type CheckBody = { batch_id?: string; karton?: number; bal?: number; slop?: number; pack?: number; label_ok?: boolean }

export function submitCheck(body: CheckBody) {
  requireRole('Terima & Cek hanya untuk Admin WH FG.', 'admin_wh_fg')
  const batch = batchOf(body.batch_id)
  if (statusCodeOf(batch) !== 'FG_READY_HANDOVER')
    fail('Batch ini tidak sedang menunggu Terima & Cek.', 'INVALID_FROM_STATUS', 409)
  const num = (value: unknown) => {
    const n = Number(value ?? 0)
    if (!Number.isFinite(n) || n < 0) fail('Jumlah hasil cek harus angka ≥ 0.')
    return Math.floor(n)
  }
  const k = num(body.karton)
  const b = num(body.bal)
  const s = num(body.slop)
  const p = num(body.pack)
  if (body.label_ok !== true) fail('Konfirmasi kondisi label unit dulu.', 'ERR_LABEL_NOT_CONFIRMED')
  const match =
    k === Number(batch.qty_karton) &&
    b === Number(batch.qty_bal) &&
    s === Number(batch.qty_slop) &&
    p === Number(batch.qty_pack)
  const result = fgFlow.run(batch, 'ACT_FG_SUBMIT', ['ACT_FG_ADMIN_FG'], {
    qty_karton_actual: k,
    qty_bal_actual: b,
    qty_slop_actual: s,
    qty_pack_actual: p,
    label_ok: true,
    checked_at: new Date().toISOString()
  })
  txLog(
    'FG Terima & Cek',
    'IFG-05',
    'fg-receive',
    String(batch.batch_no),
    match
      ? 'Hitungan fisik cocok dengan data labeler — diajukan ke Supervisor'
      : 'Selisih dengan data labeler — diajukan ke Supervisor'
  )
  return { ...result, match }
}

/** Waktu pengajuan terakhir batch ke Supervisor (log `ACT_FG_SUBMIT`), epoch ms. */
const submittedAtOf = (batch: LocalRow) =>
  readTable('wxl_submission_log')
    .filter(log => idOf(log.submission_id) === idOf(batch.submission_id) && idOf(log.action_id) === submitActionId())
    .map(log => Date.parse(String(log.date_created)))
    .sort((a, b) => b - a)[0]

/**
 * Disposisi Kadiv (BA IFG-01 §Rules, v7.50 C-13): Kadiv WH FG memutuskan batch Menunggu Approval (Setujui / Tahan /
 * Kembalikan) hanya setelah ambang Master SLA `FG_APPROVE_ESCALATE` sejak diajukan; dimatikan = tidak pernah naik ke Kadiv.
 */
registerActionGuard(WORKFLOW_CODE, ['ACT_FG_DISPOSISI_APP', 'ACT_FG_KADIV_HOLD', 'ACT_FG_KADIV_REJ'], batch => {
  if (!batch) return
  const threshold = escalateHours('FG_APPROVE_ESCALATE')
  if (threshold === null)
    fail(
      'Keputusan batch ada di Supervisor WH FG — disposisi ke Kadiv dimatikan di Master SLA.',
      'ERR_UNAUTHORIZED_ROLE',
      403
    )
  const submitted = submittedAtOf(batch)
  if (submitted && Date.now() - submitted < threshold * 3600_000)
    fail(
      `Keputusan batch ada di Supervisor WH FG. Kadiv dapat memutuskan setelah ${threshold} jam sejak diajukan.`,
      'ERR_UNAUTHORIZED_ROLE',
      403
    )
})

/** Auto-approve batch Menunggu Approval yang sudah > 8 jam (SLA_FG_HELD_AUTO) — tanpa actor manusia. */
export function slaSweep(now = Date.now()) {
  const approved: string[] = []
  /* Aturan dimatikan di Master SLA → batch menunggu keputusan manual (laporan 28 §9.1). */
  if (!slaEnabled('FG_APPROVE_AUTO')) return { approved }
  readTable('fg_labeler_batch')
    .filter(batch => statusCodeOf(batch) === 'FG_PENDING_APP')
    .forEach(batch => {
      const submitted = submittedAtOf(batch)
      if (!submitted || now - submitted < fgAutoApproveHours() * 3600_000) return
      /* Lewat `runSystemAction`: log + status + notifikasi `ALM_AUTO_APPROVED` (aturan WF-FG-01:ACT_FG_AUTO_APP). */
      runSystemAction(
        WORKFLOW_CODE,
        batch,
        'ACT_FG_AUTO_APP',
        `Disetujui otomatis — tidak ada keputusan dalam ${fgAutoApproveHours()} jam sejak diajukan.`
      )
      approved.push(String(batch.batch_no))
    })
  return { approved }
}

type Allocation = { operator_id?: string; unit_type?: string; qty?: number }

export function assignPutaway(body: { batch_id?: string; allocations?: Allocation[] }) {
  requireRole('Penugasan putaway hanya untuk Admin WH FG.', 'admin_wh_fg')
  const batch = batchOf(body.batch_id)
  const status = statusCodeOf(batch)
  if (status !== 'FG_APPROVED' && status !== 'FG_ASSIGNED')
    fail('Batch harus berstatus Disetujui sebelum dijadwalkan.', 'INVALID_FROM_STATUS', 409)
  const allocations = (body.allocations ?? []).filter(row => Number(row.qty) > 0)
  if (!allocations.length) fail('Tentukan operator dan qty yang dibawa.')
  const sku = skuOf(batch)
  const free = (level: string) => labelsOf(batch, level).filter(label => label.label_status === 'LABELED')
  const packAssigned = tasksOf(batch)
    .flatMap(itemsOf)
    .filter(item => item.unit_type === 'Pack')
    .reduce((sum, item) => sum + Number(item.qty), 0)
  const limits: Record<string, number> = {
    Karton: Math.min(actual(batch, 'karton'), free('Karton').length),
    Bal: Math.min(actual(batch, 'bal'), free('Bal').length),
    Slop: Math.min(actual(batch, 'slop'), free('Slop').length),
    Pack: actual(batch, 'pack') - packAssigned
  }
  const totals: Record<string, number> = {}
  allocations.forEach(row => {
    const type = String(row.unit_type)
    if (!(type in limits)) fail('Jenis unit tidak dikenal.')
    if (
      !readTable('directus_users').some(
        user => user.id === row.operator_id && roleCodesOf(user.id).includes('operator_wh_fg')
      )
    )
      fail('Operator tidak ada di master user WH FG.')
    totals[type] = (totals[type] ?? 0) + Number(row.qty)
  })
  Object.entries(limits).forEach(([type, limit]) => {
    if (limit > 0 && (totals[type] ?? 0) !== limit)
      fail(`Total ${type} harus pas ${limit} — dialokasikan ${totals[type] ?? 0}.`)
    if (limit <= 0 && totals[type]) fail(`Tidak ada ${type} tersisa untuk dialokasikan.`)
  })
  const reserved: Reserved = new Map()
  const pools: Record<string, LocalRow[]> = { Karton: free('Karton'), Bal: free('Bal'), Slop: free('Slop') }
  const byOperator = new Map<string, Allocation[]>()
  allocations.forEach(row =>
    byOperator.set(String(row.operator_id), [...(byOperator.get(String(row.operator_id)) ?? []), row])
  )
  const created: LocalRow[] = []
  byOperator.forEach((rows, operatorId) => {
    const task = insertRow('fg_putaway_task', {
      ...auditFieldsForCreate(),
      id: newId(),
      fpw_no: nextDocNo('FPW', 'fg_putaway_task', 'fpw_no'),
      batch_id: batch.id,
      operator_id: operatorId,
      task_status: 'ASSIGNED',
      source_ref: null,
      note: null
    })
    rows.forEach(row => {
      const type = String(row.unit_type)
      const qty = Number(row.qty)
      if (type === 'Pack') {
        insertRow('fg_putaway_task_item', {
          ...auditFieldsForCreate(),
          id: newId(),
          task_id: task.id,
          unit_type: 'Pack',
          target_pallet_id: null,
          target_container_id: containerFor('Pack')?.id ?? null,
          qty,
          override_reason: null,
          label_id: null,
          done_qty: 0,
          actual_pallet_id: null,
          actual_container_id: null,
          item_status: 'PENDING'
        })
        return
      }
      for (let i = 0; i < qty; i++) {
        const label = pools[type].shift() as LocalRow
        const target = type === 'Slop' ? containerFor('Slop') : recommendPallet(String(sku.id), type, reserved)
        updateRow('fg_label', label.id, { label_status: 'ASSIGNED', ...auditFieldsForUpdate() })
        insertRow('fg_putaway_task_item', {
          ...auditFieldsForCreate(),
          id: newId(),
          task_id: task.id,
          unit_type: type,
          target_pallet_id: type === 'Slop' ? null : (target?.id ?? null),
          target_container_id: type === 'Slop' ? (target?.id ?? null) : null,
          qty: 1,
          override_reason: null,
          label_id: label.id,
          done_qty: 0,
          actual_pallet_id: null,
          actual_container_id: null,
          item_status: 'PENDING'
        })
      }
    })
    created.push(task)
  })
  if (status === 'FG_APPROVED') fgFlow.run(batch, 'ACT_FG_ASSIGN', ['ACT_FG_ADMIN_FG'])
  txLog(
    'FG Putaway',
    'IFG-01',
    'fg-assign',
    String(batch.batch_no),
    `Tugas ${created.map(t => t.fpw_no).join(', ')} dibuat`
  )
  created.forEach(task =>
    notify({
      to: { userIds: [idOf(task.operator_id)] },
      type: 'PUTAWAY_ASSIGNED',
      title: `Tugas putaway FG ${task.fpw_no} untuk Anda`,
      body: `Batch ${batch.batch_no}: ${itemsOf(task).length} item perlu disimpan ke pallet — buka Putaway FG — Operator.`,
      link: wmsRoute('inbound-fg/putaway-fg-operator'),
      ref: { type: 'fg_putaway_task', id: String(task.id) }
    })
  )
  return { tasks: created.map(task => ({ id: task.id, fpw_no: task.fpw_no })) }
}

/* ------------------------------------------------------------ IFG-03 */

export function reassignTasks(body: { task_ids?: string[]; operator_id?: string }) {
  requireRole('Edit Assign hanya untuk Admin WH FG.', 'admin_wh_fg')
  if (!body.operator_id) fail('Pilih operator tujuan dulu.')
  if (
    !readTable('directus_users').some(
      user => user.id === body.operator_id && roleCodesOf(user.id).includes('operator_wh_fg')
    )
  )
    fail('Operator tidak ada di master user WH FG.')
  const ids = body.task_ids ?? []
  if (!ids.length) fail('Pilih minimal satu tugas.')
  ids.forEach(id => {
    const task = findRow('fg_putaway_task', id)
    if (!task) fail('Tugas tidak ditemukan.', 'ERR_NOT_FOUND', 404)
    if (task.task_status === 'COMPLETED') fail(`Tugas ${task.fpw_no} sudah selesai — tidak bisa dialihkan.`)
    if (itemsOf(task).some(item => item.item_status === 'DONE'))
      fail(`Tugas ${task.fpw_no} sudah sebagian dikerjakan — tidak bisa dialihkan.`)
    updateRow('fg_putaway_task', id, { operator_id: body.operator_id, ...auditFieldsForUpdate() })
    txLog('FG Putaway', 'IFG-03', 'fg-reassign', String(task.fpw_no), 'Tugas dialihkan ke operator lain')
    notify({
      to: { userIds: [body.operator_id] },
      type: 'PUTAWAY_ASSIGNED',
      title: `Tugas putaway FG ${task.fpw_no} dialihkan ke Anda`,
      body: `Tugas ${task.fpw_no} dialihkan oleh Admin WH FG — buka Putaway FG — Operator.`,
      link: wmsRoute('inbound-fg/putaway-fg-operator'),
      ref: { type: 'fg_putaway_task', id: String(task.id) }
    })
  })
  return { moved: ids.length }
}

/* ------------------------------------------------------------ IFG-04 */

function taskFor(taskId: unknown, mustOwn = true) {
  const task = findRow('fg_putaway_task', String(taskId ?? ''))
  if (!task) fail('Tugas tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (mustOwn) {
    requireRole('Putaway FG dikerjakan Operator WH FG.', 'operator_wh_fg')
    if (idOf(task.operator_id) !== currentUserId()) fail('Tugas ini milik operator lain.', 'ERR_NOT_OWNER', 403)
  }
  return task
}

const isClaimTask = (task: LocalRow) => task.source_type === 'CLAIM'
/** SKU tugas: batch produksi, atau `sku_id` langsung untuk tugas sumber claim (tanpa batch). */
const skuIdOfTask = (task: LocalRow) => (isClaimTask(task) ? idOf(task.sku_id) : idOf(batchOf(task.batch_id).sku_id))
const lockMessage = (item: LocalRow) => `Barang ini hanya boleh disimpan di ${targetCodeOf(item)}.`
const CLAIM_ZONE_TYPES = ['RETURN_CLAIM', 'NG_FG']
const isClaimZone = (code: string) => {
  const pallet = palletByCode(code)
  return Boolean(
    pallet && CLAIM_ZONE_TYPES.includes(String(findRow('warehouse_zone', idOf(pallet.zone_id))?.zone_type))
  )
}
const claimZoneReject = (code: string): GateVerdict => ({
  verdict: 'reject',
  title: 'Pallet Khusus Claim',
  message: `${code} khusus barang claim Repack / Not Good — simpan Good di penyimpanan FG biasa.`,
  location_kind: 'pallet'
})
const lockedReject = (item: LocalRow): GateVerdict => ({
  verdict: 'reject',
  title: 'Tujuan Terkunci',
  message: lockMessage(item),
  location_kind: 'pallet',
  expected: targetCodeOf(item)
})

export function putawayGate(body: { task_id?: string; unit_type?: string; location_code?: string }) {
  const task = taskFor(body.task_id, false)
  const type = String(body.unit_type ?? '')
  const code = up(body.location_code)
  const pending = itemsOf(task).filter(item => item.item_status !== 'DONE')
  if (isClaimTask(task)) {
    const here = pending.filter(item => up(targetCodeOf(item)) === code)
    if (here.some(item => item.is_target_locked))
      return {
        verdict: 'ok' as const,
        title: 'Tujuan Claim',
        message: '',
        location_kind: palletByCode(code) ? ('pallet' as const) : ('container' as const)
      }
    const free = pending.find(item => !item.is_target_locked && (item.unit_type === type || here.includes(item)))
    const unlocked = pending.find(item => !item.is_target_locked)
    const locked = pending.find(item => item.is_target_locked)
    /* pallet Zone Retur Claim / Area NG FG hanya untuk barang claim yang dikunci ke sana */
    if (isClaimZone(code)) return locked ? lockedReject(locked) : claimZoneReject(code)
    /* tinggal barang terkunci → lokasi lain mana pun ditolak dengan pesan kuncinya */
    if (!unlocked && locked) return lockedReject(locked)
    /* barang bebas (Good) mengikuti aturan lokasi produksi biasa */
    const probe = free ?? unlocked ?? pending[0]
    return checkLocation(
      task,
      skuIdOfTask(task),
      String(probe?.unit_type ?? type),
      code,
      probe ? targetCodeOf(probe) : ''
    )
  }
  const rec = pending.find(item => item.unit_type === type)
  return checkLocation(task, skuIdOfTask(task), type, code, rec ? targetCodeOf(rec) : '')
}

function completeIfDone(task: LocalRow) {
  const items = itemsOf(task)
  const done = items.every(item => item.item_status === 'DONE')
  updateRow('fg_putaway_task', task.id, { task_status: done ? 'COMPLETED' : 'IN_PROGRESS', ...auditFieldsForUpdate() })
  if (!done) return false
  if (isClaimTask(task)) {
    checkClaimComplete(idOf(task.claim_id))
    return true
  }
  const batch = batchOf(task.batch_id)
  if (statusCodeOf(batch) !== 'FG_ASSIGNED') return true
  const allTasksDone = tasksOf(batch).every(row => findRow('fg_putaway_task', row.id)?.task_status === 'COMPLETED')
  const labelsLeft = labelsOf(batch).some(label => label.label_status === 'LABELED')
  if (allTasksDone && !labelsLeft) fgFlow.run(batch, 'ACT_FG_COMPLETE', ['ACT_FG_OP_FG'])
  return true
}

export function putawayPlace(body: {
  task_id?: string
  item_id?: string
  location_code?: string
  override_reason?: string
}) {
  const task = taskFor(body.task_id)
  const item = findRow('fg_putaway_task_item', String(body.item_id ?? ''))
  if (!item || idOf(item.task_id) !== task.id) fail('Unit bukan bagian dari tugas ini.', 'ERR_NOT_IN_TASK')
  if (item.item_status === 'DONE') fail('Unit ini sudah disimpan.')
  const skuId = skuIdOfTask(task)
  const code = up(body.location_code)
  const unitType = String(item.unit_type)
  if (item.is_target_locked && code !== up(targetCodeOf(item))) fail(lockMessage(item), 'ERR_TARGET_LOCKED', 422)
  if (isClaimTask(task) && isClaimZone(code)) fail(claimZoneReject(code).message, 'ERR_LOCATION_REJECTED')
  const gate = checkLocation(task, skuId, unitType, code, targetCodeOf(item))
  if (gate.verdict === 'reject') fail(gate.message, 'ERR_LOCATION_REJECTED')
  const reason = String(body.override_reason ?? '').trim()
  if (gate.verdict === 'needs_reason' && !reason) fail('Alasan wajib dicatat untuk penempatan ini.', 'ERR_NEEDS_REASON')
  const pallet = palletByCode(code)
  const container = containerByCode(code)
  const label = findRow('fg_label', idOf(item.label_id)) as LocalRow
  if (pallet) {
    updateRow('pallet', pallet.id, {
      occupied: Number(pallet.occupied ?? 0) + 1,
      sku_id: skuId,
      unit_type: unitType,
      pallet_status: 'IN_USE',
      ...auditFieldsForUpdate()
    })
  }
  updateRow('fg_label', label.id, {
    label_status: 'IN_STOCK',
    pallet_id: pallet?.id ?? null,
    container_id: container?.id ?? null,
    zone_id: pallet ? idOf(pallet.zone_id) : null,
    /* unit Good utuh dari claim kembali ke stok dengan tanda Retur (lot prioritas) */
    ...(isClaimTask(task) ? { is_priority_lot: true } : {}),
    ...auditFieldsForUpdate()
  })
  updateRow('fg_putaway_task_item', item.id, {
    item_status: 'DONE',
    done_qty: 1,
    actual_pallet_id: pallet?.id ?? null,
    actual_container_id: container?.id ?? null,
    override_reason: gate.verdict === 'needs_reason' ? reason : null,
    ...auditFieldsForUpdate()
  })
  txLog(
    'FG Putaway',
    'IFG-04',
    'fg-putaway',
    String(label.label_no),
    `${unitType} → ${code}${isClaimTask(task) ? ` · claim ${String(task.source_ref ?? '')} (Retur)` : ''}${reason ? ` · alasan: ${reason}` : ''}`
  )
  const taskDone = completeIfDone(task)
  return { item_id: item.id, location: code, task_done: taskDone }
}

/** Pack claim yang ditaruh: Good / Repack → stok pack berlabel (tanda Retur / Repack), Not Good → karantina NG FG. */
function postClaimPack(
  task: LocalRow,
  item: LocalRow,
  location: { pallet?: LocalRow; container?: LocalRow },
  qty: number,
  done: boolean
) {
  const claim = findRow('claim', idOf(task.claim_id))
  if (item.category === 'NG') {
    const pack = findRow('fg_ng_pack', idOf(item.ng_pack_id))
    if (pack && done)
      updateRow('fg_ng_pack', String(pack.id), {
        ng_status: 'IN_QUARANTINE',
        pallet_id: location.pallet?.id ?? null,
        queued_at: new Date().toISOString(),
        current_area: String(findRow('warehouse_zone', idOf(location.pallet?.zone_id))?.code ?? 'ZN-01'),
        ...auditFieldsForUpdate()
      })
    return
  }
  const placeId = location.pallet?.id ?? location.container?.id
  const stock = readTable('fg_pack_stock').find(
    row =>
      row.label_no === item.label_no &&
      (idOf(row.pallet_id) === placeId || idOf(row.container_id) === placeId) &&
      row.status === 'IN_STOCK'
  )
  if (stock) {
    updateRow('fg_pack_stock', stock.id, { qty_pack: Number(stock.qty_pack) + qty, ...auditFieldsForUpdate() })
    return
  }
  insertRow('fg_pack_stock', {
    ...auditFieldsForCreate(),
    id: newId(),
    sku_id: idOf(task.sku_id),
    batch_id: idOf(item.batch_id) || null,
    container_id: location.container?.id ?? null,
    pallet_id: location.pallet?.id ?? null,
    claim_id: claim?.id ?? null,
    claim_item_id: ((item.source_item_ids ?? []) as string[])[0] ?? null,
    claim_category: item.category,
    lot_no: claim?.lot_no ?? null,
    is_priority_lot: true,
    origin: item.category === 'REPACK' ? REPACK_ORIGIN : 'Retur',
    label_no: item.label_no,
    qty_pack: qty,
    status: 'IN_STOCK'
  })
}

export function putawayPlacePack(body: { task_id?: string; item_id?: string; location_code?: string; qty?: number }) {
  const task = taskFor(body.task_id)
  const item = findRow('fg_putaway_task_item', String(body.item_id ?? ''))
  if (!item || idOf(item.task_id) !== task.id || item.unit_type !== 'Pack')
    fail('Item Pack bukan bagian dari tugas ini.')
  const qty = Number(body.qty)
  if (!Number.isFinite(qty) || qty <= 0) fail('Qty harus lebih dari 0.')
  const remaining = Number(item.qty) - Number(item.done_qty ?? 0)
  if (qty > remaining) fail(`Qty melebihi sisa tugas — sisa ${remaining}.`)
  const code = up(body.location_code)
  const claimTask = isClaimTask(task)
  if (item.is_target_locked && code !== up(targetCodeOf(item))) fail(lockMessage(item), 'ERR_TARGET_LOCKED', 422)
  /* tujuan terkunci claim: aturan pallet produksi (Pack hanya di container) tidak berlaku */
  const gate = item.is_target_locked
    ? ({ verdict: 'ok' } as GateVerdict)
    : checkLocation(task, skuIdOfTask(task), 'Pack', code, targetCodeOf(item))
  if (gate.verdict === 'reject') fail(gate.message, 'ERR_LOCATION_REJECTED')
  const pallet = palletByCode(code)
  const container = containerByCode(code) as LocalRow
  const done = Number(item.done_qty ?? 0) + qty
  if (claimTask)
    postClaimPack(
      task,
      item,
      { pallet: item.is_target_locked ? pallet : undefined, container },
      qty,
      done >= Number(item.qty)
    )
  else {
    const batch = batchOf(task.batch_id)
    const stock = readTable('fg_pack_stock').find(
      row => idOf(row.batch_id) === batch.id && idOf(row.container_id) === container.id
    )
    if (stock)
      updateRow('fg_pack_stock', stock.id, { qty_pack: Number(stock.qty_pack) + qty, ...auditFieldsForUpdate() })
    else
      insertRow('fg_pack_stock', {
        ...auditFieldsForCreate(),
        id: newId(),
        sku_id: batch.sku_id,
        batch_id: batch.id,
        container_id: container.id,
        qty_pack: qty
      })
  }
  updateRow('fg_putaway_task_item', item.id, {
    done_qty: done,
    item_status: done >= Number(item.qty) ? 'DONE' : 'PENDING',
    actual_pallet_id: item.is_target_locked ? (pallet?.id ?? null) : null,
    actual_container_id: item.is_target_locked ? null : (container?.id ?? null),
    override_reason: gate.verdict === 'needs_reason' ? 'Container bukan rekomendasi' : null,
    ...auditFieldsForUpdate()
  })
  txLog(
    'FG Putaway',
    'IFG-04',
    'fg-putaway-pack',
    claimTask ? String(item.label_no ?? task.source_ref) : String(batchOf(task.batch_id).batch_no),
    `Pack ${qty} → ${code}${claimTask ? ` · claim ${String(task.source_ref ?? '')} (${item.category === 'REPACK' ? 'Repack' : item.category === 'NG' ? 'Not Good' : 'Retur'})` : ''}`
  )
  return { done_qty: done, task_done: completeIfDone(task) }
}

export function rackFull(body: { task_id?: string; location_code?: string }) {
  const task = taskFor(body.task_id)
  const pallet = palletByCode(up(body.location_code))
  if (!pallet) fail('Rack Penuh hanya untuk pallet.')
  if (itemsOf(task).some(item => item.is_target_locked && idOf(item.target_pallet_id) === (pallet as LocalRow).id))
    fail('Tujuan claim terkunci — pallet ini tidak bisa diganti dari layar putaway.', 'ERR_TARGET_LOCKED', 422)
  updateRow('pallet', (pallet as LocalRow).id, { pallet_status: 'FULL', ...auditFieldsForUpdate() })
  const reserved: Reserved = new Map()
  const retargeted: string[] = []
  itemsOf(task)
    .filter(item => item.item_status !== 'DONE' && idOf(item.target_pallet_id) === (pallet as LocalRow).id)
    .forEach(item => {
      const next = recommendPallet(skuIdOfTask(task), String(item.unit_type), reserved, [
        String((pallet as LocalRow).code)
      ])
      updateRow('fg_putaway_task_item', item.id, { target_pallet_id: next?.id ?? null, ...auditFieldsForUpdate() })
      if (next) retargeted.push(String(next.code))
    })
  txLog(
    'FG Putaway',
    'IFG-04',
    'fg-rack-full',
    String((pallet as LocalRow).code),
    'Rack penuh — sisa unit dialihkan ke rekomendasi berikutnya'
  )
  return { retargeted: [...new Set(retargeted)] }
}

export function correctPlacement(body: { label_code?: string; location_code?: string }) {
  requireRole(
    'Koreksi penempatan FG hanya oleh Operator WH FG, Admin WH FG, atau Supervisor WH FG.',
    'operator_wh_fg',
    'admin_wh_fg',
    'supervisor_wh_fg'
  )
  const label = readTable('fg_label').find(row => up(row.label_no) === up(body.label_code))
  if (!label) fail('Label tidak dikenal.', 'ERR_NOT_FOUND', 404)
  if (label.label_status !== 'IN_STOCK') fail('Label ini belum tersimpan di rak.')
  const code = up(body.location_code)
  const pallet = palletByCode(code)
  const container = containerByCode(code)
  if (!pallet && !container) fail(`${code} bukan pallet atau container FG.`)
  const level = String(label.level)
  const currentPallet = findRow('pallet', idOf(label.pallet_id))
  const currentContainer = findRow('container', idOf(label.container_id))
  if (pallet && currentPallet && pallet.id === currentPallet.id) fail('Pallet sama dengan catatan — tidak ada koreksi.')
  if (container && currentContainer && container.id === currentContainer.id)
    fail('Container sama dengan catatan — tidak ada koreksi.')
  const batch = batchOf(label.batch_id)
  const gate = checkLocation({ id: '' } as LocalRow, idOf(batch.sku_id), level, code, code)
  if (gate.verdict === 'reject') fail(gate.message, 'ERR_LOCATION_REJECTED')
  if (currentPallet)
    updateRow('pallet', currentPallet.id, {
      occupied: Math.max(0, Number(currentPallet.occupied ?? 0) - 1),
      ...(Number(currentPallet.occupied ?? 0) - 1 <= 0
        ? { sku_id: null, unit_type: null, pallet_status: 'EMPTY' }
        : {}),
      ...auditFieldsForUpdate()
    })
  if (pallet)
    updateRow('pallet', pallet.id, {
      occupied: Number(pallet.occupied ?? 0) + 1,
      sku_id: batch.sku_id,
      unit_type: level,
      pallet_status: 'IN_USE',
      ...auditFieldsForUpdate()
    })
  updateRow('fg_label', label.id, {
    pallet_id: pallet?.id ?? null,
    container_id: container?.id ?? null,
    zone_id: pallet ? idOf(pallet.zone_id) : null,
    ...auditFieldsForUpdate()
  })
  txLog(
    'FG Putaway',
    'IFG-04',
    'fg-correct',
    String(label.label_no),
    `${currentPallet?.code ?? currentContainer?.code ?? '—'} → ${code}`
  )
  return { label_no: label.label_no, from: currentPallet?.code ?? currentContainer?.code ?? null, to: code }
}

/* ------------------------------------------------------------ registrasi */

export function registerFgInboundHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/inbound-fg'
  mock.onPost(`${base}/refresh-labeler`).reply(() => handle(() => refreshLabeler()))
  mock.onPost(`${base}/submit-check`).reply(config => handle(() => submitCheck(parse(config.data))))
  mock.onPost(`${base}/sla-sweep`).reply(() => handle(() => slaSweep()))
  mock.onPost(`${base}/assign-putaway`).reply(config => handle(() => assignPutaway(parse(config.data))))
  mock.onPost(`${base}/reassign`).reply(config => handle(() => reassignTasks(parse(config.data))))
  mock.onPost(`${base}/putaway-gate`).reply(config => handle(() => putawayGate(parse(config.data))))
  mock.onPost(`${base}/putaway-place`).reply(config => handle(() => putawayPlace(parse(config.data))))
  mock.onPost(`${base}/putaway-place-pack`).reply(config => handle(() => putawayPlacePack(parse(config.data))))
  mock.onPost(`${base}/rack-full`).reply(config => handle(() => rackFull(parse(config.data))))
  mock.onPost(`${base}/correct-placement`).reply(config => handle(() => correctPlacement(parse(config.data))))
}
